// The wind arrow length ladder: which speed draws which arrow.
//
// Length carries speed, so the bands decide what the arrow field can say. They
// are not evenly spaced across the palette's 0-30 m/s, because the data does not
// live there: these are multi-year blended daily means read off an interpolated
// grid, so the tails are smoothed away long before 30 — the median sits near
// 4 m/s, the 99th percentile near 11, and fewer than one cell in three thousand
// reaches 17. Spacing eight bands evenly would spend most of them on speeds that
// never occur, and a Beaufort-shaped guess (2/4/6/9/12/17) was tried first and
// put two thirds of every view into two bands while three more sat empty.
//
// So the edges are the data's own quantiles, rounded to numbers a reader can
// hold, which is what gives every length real work to do. The shares each band
// actually carries are asserted in wind-bands.test.ts rather than written down
// here, because they move whenever a year is added to the blend.
//
// Pure: the ladder and the lookup only. Rasterizing an arrow is
// client/map/wind-sprites.ts, which builds its sprites and its MapLibre step
// expression from this table so the two cannot drift.

// One band: the arrow drawn for every speed below `max`, and the shaft length
// (in CSS px) that carries it.
export interface ArrowBin {
  id: string;
  max: number;
  shaft: number;
}

export const ARROW_BINS: readonly ArrowBin[] = [
  { id: 'wind-1', max: 1.5, shaft: 8 },
  { id: 'wind-2', max: 2.5, shaft: 11 },
  { id: 'wind-3', max: 3.5, shaft: 14 },
  { id: 'wind-4', max: 5, shaft: 17 },
  { id: 'wind-5', max: 7, shaft: 21 },
  { id: 'wind-6', max: 10, shaft: 25 },
  { id: 'wind-7', max: Infinity, shaft: 30 },
];

// Below this, direction is the atan2 of two averages that have both gone to
// nearly zero — noise. A dot says "still" without inventing a bearing, the same
// way sampleAt refuses to interpolate across a gap rather than invent a value.
export const CALM_MAX = 0.5;

export const CALM_ID = 'wind-calm';

// The sprite one speed draws. Calm below CALM_MAX; otherwise the first band the
// speed falls under, and the open-ended last band catches everything above.
export function arrowImageFor(ws: number): string {
  if (!(ws >= CALM_MAX)) return CALM_ID; // NaN included: no reading, no bearing
  for (const b of ARROW_BINS) {
    if (ws < b.max) return b.id;
  }
  return ARROW_BINS[ARROW_BINS.length - 1].id;
}
